import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import {
  purchaseInformationApi,
  UpdatePurchaseInformationRequest,
} from '@texas/api/endpoints/purchaseInformationApi';
import { suppliersApi } from '@texas/api/endpoints/suppliersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { TexasInputWrapper } from '@texas/components/shared/TexasInputWrapper';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { CurrencyRate } from '@texas/components/shared/CurrencyRate';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { ReactSelectOptionWithBadges } from '@texas/types';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { setValueAsDecimalExtender } from '../sales/shared';

interface FormData {
  currencyId: number | null;
  rate: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  purchaseInformationId: number;
  currencyCode: string;
  exchangeRate: number;
  branchId: number;
  supplierId: number;
  supplierName: string;
  onSaved: () => void;
}

export function EditPurchaseInfoDrawer({
  isOpen,
  onClose,
  purchaseInformationId,
  currencyCode,
  exchangeRate,
  branchId,
  supplierId,
  supplierName,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const {
    useCurrenciesData: { findCurrency },
    branchesData: [, getBranches, , findBranch],
  } = useMetadata();
  const { control, register, reset, handleSubmit, setValue, formState } =
    useForm<FormData>({
      defaultValues: {
        currencyId: null,
        rate: exchangeRate,
      },
      mode: 'onChange',
    });

  const { request: updateRequest, loading } = useApiRequest(
    purchaseInformationApi.updatePurchaseInformationV2,
  );

  const rateError = formState.errors.rate;
  const currencyId = useWatch({ control, name: 'currencyId' });
  const rate = useWatch({ control, name: 'rate' });

  const { data: mergedCurrencies, refetch: refetchMergedCurrencies } =
    useApiResource(suppliersApi.getSupplierCurrenciesForBranch);

  useEffect(() => {
    getBranches();
    refetchMergedCurrencies(supplierId, branchId);
  }, [getBranches, refetchMergedCurrencies, supplierId, branchId]);

  // Reset form when drawer opens or underlying values change
  useEffect(() => {
    if (!isOpen) return;
    const matched = mergedCurrencies?.find((c) => c.code === currencyCode);
    reset({
      currencyId: matched?.id ?? null,
      rate: exchangeRate,
    });
  }, [isOpen, currencyCode, exchangeRate, mergedCurrencies, reset]);

  async function onSubmit(data: FormData) {
    const req: UpdatePurchaseInformationRequest = {
      fixedExchangeRate: data.rate,
      currencyId: data.currencyId,
    };
    if (data.currencyId != null) req.currencyId = data.currencyId;

    const res = await updateRequest(purchaseInformationId, req);
    if (!res.hasError) {
      onSaved();
      onClose();
    }
  }

  const selectedCurrency = mergedCurrencies?.find((c) => c.id === currencyId);
  const isDefaultCurrency = selectedCurrency?.isDefaultBranchCurrency ?? false;
  const branchName = findBranch(branchId)?.name ?? '';

  function handleRefreshRate(value: number) {
    setValue('rate', value);
  }

  const currencyOptions =
    mergedCurrencies?.map<ReactSelectOptionWithBadges>((c) => ({
      label: `${c.code} (${c.branchBuyingRate})`,
      value: c.id,
      badge1: c.isSupplierCustom
        ? { visible: true, label: `Supplier: ${c.buyingRate}` }
        : undefined,
    })) ?? [];

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="sm">
      <DrawerOverlay />
      <DrawerContent bg="texas.bg.900" _light={{ bg: 'gray.10' }}>
        <DrawerCloseButton />
        <DrawerHeader>{t('variant.supplier.editPurchaseInfo')}</DrawerHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerBody>
            <Flex direction="column" gap={4}>
              <Text fontSize="sm" color="gray.400">
                {t('variant.supplier.editPurchaseInfoFor', {
                  supplierName,
                  branchName,
                })}
              </Text>
              <TexasInputWrapper label={t('general.currency')}>
                <Controller
                  name="currencyId"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TexasSelect<ReactSelectOptionWithBadges>
                      showBadges={true}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      menuPortalTarget={document.body}
                      isClearable={false}
                      value={
                        value != null && findCurrency(value)
                          ? {
                              label: `${findCurrency(value)!.code} (${
                                findCurrency(value)!.buyingRate
                              })`,
                              value: value,
                            }
                          : null
                      }
                      options={currencyOptions}
                      onChange={(e) => {
                        if (!e) return;
                        onChange(e.value);
                        const currency = mergedCurrencies?.find(
                          (c) => c.id === e.value,
                        );
                        if (currency) {
                          const rate = currency.isSupplierCustom
                            ? currency.buyingRate
                            : currency.branchBuyingRate;
                          setValue('rate', rate);
                        }
                      }}
                    />
                  )}
                />
              </TexasInputWrapper>
              <TexasInputWrapper
                label={t('variant.supplier.exchangeRate')}
                error={rateError}
                utilButtons={
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="ghost"
                      isDisabled={isDefaultCurrency || !selectedCurrency}
                    >
                      {t('variant.supplier.refreshRate')}
                    </MenuButton>
                    <MenuList>
                      {selectedCurrency?.isSupplierCustom && (
                        <MenuItem
                          onClick={() =>
                            handleRefreshRate(selectedCurrency.buyingRate)
                          }
                          fontSize="sm"
                          isDisabled={rate === selectedCurrency.buyingRate}
                        >
                          {t('variant.supplier.pullFromSupplier')} (
                          {selectedCurrency.buyingRate})
                        </MenuItem>
                      )}
                      {selectedCurrency && (
                        <MenuItem
                          onClick={() =>
                            handleRefreshRate(selectedCurrency.branchBuyingRate)
                          }
                          fontSize="sm"
                          isDisabled={
                            rate === selectedCurrency.branchBuyingRate
                          }
                        >
                          {t('variant.supplier.pullFromBranch')} (
                          {selectedCurrency.branchBuyingRate})
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                }
              >
                <Input
                  variant="texas"
                  isDisabled={isDefaultCurrency}
                  {...register('rate', {
                    required: t('variant.supplier.exchangeRateRequired'),
                    validate: (value) =>
                      value > 0 ||
                      t('variant.supplier.exchangeRateGreaterThanZero'),
                    setValueAs: (v) => setValueAsDecimalExtender(v, false),
                  })}
                />
              </TexasInputWrapper>
              {isDefaultCurrency && (
                <Text fontSize="xs" color="gray.500">
                  {t('variant.supplier.branchDefaultCurrencyLocked')}
                </Text>
              )}
              <CurrencyRate
                branchId={branchId}
                currencyId={currencyId}
                exchangeRate={rate}
                exchangeRateType="purchase"
                supplierId={supplierId}
              />
            </Flex>
          </DrawerBody>
          <DrawerFooter gap={2}>
            <Button variant="texas-light" onClick={onClose}>
              {t('general.cancel')}
            </Button>
            <Button variant="texas-solid" isLoading={loading} type="submit">
              {t('general.save')}
            </Button>
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
